// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';
import * as L from 'leaflet';
import G from 'google-map-react';

import { WidgetGrid, JarvisWidget } from 'wdna-components/lib';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import { removeTab } from 'wdna-components/lib/tabs';
import Loader from 'wdna-components/lib/loaders/local/Loader';
import UiValidate from 'wdna-components/lib/forms/validation/UiValidate';
import MapModal from 'wdna-components/lib/modals/generics/MapModal';
import { messageError, messageInfo } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import Form from './Form';
import ButtonsForm from '../ButtonsForm';

import { location, LocationDO } from '../../../requests';

interface Props {
  rowId: number;
  notify?: () => void;
}

interface State {
  loading?: boolean;
  showMap: boolean;

  data: Partial<LocationDO>;
}

export default class Edit extends React.Component<Props, State> {
  private pid: any;
  private defaultValues: any;
  private mounted: boolean;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;
    this.defaultValues = {};

    this.state = {
      loading: false,
      showMap: false,

      data: {},
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
    this.load().catch(() => { /* pass */ });
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public load = async (): Promise<void> => {
    const { rowId } = this.props || {};
    const isNew: boolean = !!isNaN(rowId);

    this.loadingStart();
    try {
      let rowData: Partial<LocationDO> = this.state.data || {};
      if (!isNew) {
        rowData = await location<Partial<LocationDO>>('get', String(rowId)) || {};
      }
      this.setState(
        {
          data: { ...rowData, },
        },
        () => {
          this.defaultValues = _.cloneDeep(rowData);
        }
      );
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public onReset = (): void => {
    this.setState({
      data: {
        ...this.defaultValues,
      }
    });
  };

  public submit = (event: React.FormEvent<HTMLFormElement>): any => {
    event.preventDefault();
    const { data } = this.state;

    this.loadingStart();
    location('set', undefined, data)
      ?.then(() => {
        messageInfo('Success', `Location successfully ${data?.id ? 'edited' : 'created'} ${data.name}`, 5000);
        const { notify } = this.props || {};
        if (typeof notify === 'function') {
          notify();
        }
        window.store.dispatch(removeTab('LE'));
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { value, name } = event.target;
    this.setState((prevState: State) => ({
      data: {
        ...prevState.data,
        [name]: value,
      },
    }));
  };

  public handleMapClick = (e: L.LeafletMouseEvent | G.ClickEventValue): void => {
    const lat: number = (e as G.ClickEventValue).lat || (e as L.LeafletMouseEvent).latlng.lat;
    const lng: number = (e as G.ClickEventValue).lng || (e as L.LeafletMouseEvent).latlng.lng;
    this.setState((prevState: State) => ({
      data: {
        ...prevState.data,
        latitude: lat,
        longitude: lng,
      },
    }));
  };

  public showMap = (): void => {
    this.setState({
      showMap: true,
    });
  };

  public closeMap = (): void => {
    this.setState({
      showMap: false,
    });
  };

  public loadingStart = (): void => {
    this.setState({
      loading: true,
    });
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    this.setState({
      loading: false,
    });
    window.store.dispatch(finishProcess(this.pid));
  };

  public render(): JSX.Element {
    const { data, loading } = this.state;

    let mapZoom: number = 6;
    let mapInitialMapCenter: [number, number] = [40.6, 1.13];
    if (_.isNumber(data?.latitude) && _.isNumber(data?.longitude)) {
      mapZoom = 10;
      mapInitialMapCenter = [data.latitude, data.longitude];
    }

    return (
      <div id="content">
        <WidgetGrid>
          <div className="row">
            <article className="col-sm-12 col-md-12 col-lg-12">
              <JarvisWidget
                editbutton={false}
                togglebutton={false}
                colorbutton={false}
                deletebutton={false}
                custombutton={false}
                fullscreenbutton={false}
              >
                <div className="widget-body">
                  <Loader show={loading} overlay={false} />
                  <UiValidate>
                    <form className="smart-form" onSubmit={this.submit} method="post" encType="multipart/form-data">
                      <header>
                        <h2>
                          <span className="widget-icon" style={{ padding: '8px' }}>
                            <i className="fa fa-edit" />
                          </span>
                          Location {data?.id ? `Edit: ${data.id}` : 'New'}
                        </h2>
                      </header>
                      <Form
                        formValues={this.state}
                        onChange={this.handleChange}
                        showMap={this.showMap}
                      />
                      <MapModal
                        zoom={mapZoom}
                        initialMapCenter={mapInitialMapCenter}
                        latitude={data?.latitude || 0}
                        longitude={data?.longitude || 0}
                        onMapClick={this.handleMapClick}
                        title="Select coordinates of the location"
                        show={this.state.showMap}
                        onCancel={this.closeMap}
                        useGoogleMaps={false}
                      />
                      <ButtonsForm formValues={this.state} onReset={this.onReset} />
                    </form>
                  </UiValidate>
                </div>
              </JarvisWidget>
            </article>
          </div>
        </WidgetGrid>
      </div>
    );
  }
}
